import type { ReactNode } from 'react'
import { buildAppHref, useMarketingLocale } from '~/lib/marketing-locale'
import { cn } from '~/lib/utils'

type CtaButtonProps = {
  children: ReactNode
  href?: string
  /** An app route — `/sign-up` or `/sign-in`. Always a full page load. */
  to?: string
  /** Extra query parameters for an app-route target. */
  search?: Record<string, string>
  variant?: 'yellow' | 'dark' | 'soft'
  size?: 'md' | 'lg'
  className?: string
}

const VARIANTS = {
  dark: 'bg-[#1d1d20] text-white hover:bg-[#000]',
  soft: 'bg-[#f0f0f1] text-[#0b0b0d] hover:bg-[#e7e7e8]',
  yellow: 'bg-[#ffde18] text-[#0b0b0d] hover:brightness-[0.97]',
} as const

const SIZES = {
  lg: 'px-8 py-4 text-[15px]',
  md: 'px-5 py-2 text-[14px]',
} as const

/** Pill-shaped call-to-action button. App routes (`to`) render a plain anchor
 * via `buildAppHref` — crossing into the app is a full page load either way,
 * and the anchor is what keeps `?lang=` intact. `href` covers in-page
 * anchors, mailtos and externals. */
export const CtaButton = ({
  children,
  href,
  to,
  search,
  variant = 'yellow',
  size = 'md',
  className,
}: CtaButtonProps) => {
  const locale = useMarketingLocale()
  const classes = cn(
    'inline-flex select-none items-center justify-center rounded-full font-semibold transition',
    VARIANTS[variant],
    SIZES[size],
    className
  )

  return (
    <a className={classes} href={to ? buildAppHref({ locale, search, to }) : (href ?? '#')}>
      {children}
    </a>
  )
}
